import React from 'react';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import {
  dewormingApi, diseaseOutbreaksApi, growthRecordsApi, healthRecordsApi, medicineMasterApi,
  treatmentsApi, vaccinationSchedulesApi, vaccinationsApi, vaccineMasterApi, vetVisitsApi,
} from '../../services/resources';
import { formatCurrency, formatDate, formatNumber, today } from '../../utils/format';
import { AnimalTag } from '../../features/common/AnimalTag';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import type {
  Deworming, DiseaseOutbreak, GrowthRecord, HealthRecord, MedicineMaster, Treatment,
  VaccinationSchedule, Vaccination, VaccineMaster, VetVisit,
} from '../../types';
import type { ModuleConfig } from '../../features/common/types';

// Page-vs-tab call: v2 expanded Health from 3 v1 tables to 11 (REQ-012/013/014).
// All of them are steps of the same clinical workflow (WF-04: symptom -> vet ->
// diagnosis -> treatment -> medicine stock -> follow-up) or its supporting
// catalogues, so they stay as tabs on one "Health" page.

const healthRecordsConfig: ModuleConfig<HealthRecord> = {
  title: 'Health Records', entityLabel: 'health record', module: 'Health', queryKey: 'health-records', api: healthRecordsApi, idKey: 'id',
  referenceKeys: ['animals'], defaultValues: { record_date: today() },
  columns: [
    { key: 'animal', header: 'Animal', primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'date', header: 'Date', render: (r) => formatDate(r.record_date) },
    { key: 'disease', header: 'Disease', render: (r) => r.disease || '—' },
    { key: 'severity', header: 'Severity', render: (r) => r.severity || '—' },
    { key: 'outcome', header: 'Outcome', render: (r) => r.outcome || '—' },
  ],
  fields: [
    { name: 'animal_id', label: 'Animal', type: 'select', section: 'Record', required: true, optionsKey: 'animals' },
    { name: 'record_date', label: 'Date', type: 'date', section: 'Record', required: true },
    { name: 'disease', label: 'Disease', type: 'text', section: 'Record', optional: true },
    { name: 'severity', label: 'Severity', type: 'text', section: 'Record', optional: true },
    { name: 'symptoms', label: 'Symptoms', type: 'textarea', section: 'Findings', optional: true, fullWidth: true },
    { name: 'diagnosis', label: 'Diagnosis', type: 'textarea', section: 'Findings', optional: true, fullWidth: true },
    { name: 'outcome', label: 'Outcome', type: 'text', section: 'Findings', optional: true },
  ],
};

// Done once a later visit for the same animal is logged; otherwise the
// days left (or overdue) to the follow-up date the vet gave.
function FollowUpCell({ visit }: { visit: VetVisit }) {
  if (!visit.follow_up_date) return <>—</>;
  if (visit.follow_up_done) return <ILMSStatusBadge status="Done" tone="success" size="sm" />;
  const days = Math.round((new Date(visit.follow_up_date).getTime() - new Date(today()).getTime()) / 86_400_000);
  const label = days === 0 ? 'Due today' : days > 0 ? `In ${days} day${days === 1 ? '' : 's'}` : `Overdue ${-days} day${days === -1 ? '' : 's'}`;
  return (
    <span className={days < 0 ? 'text-critical-700 font-medium' : 'text-ink-700'}>
      {formatDate(visit.follow_up_date)} · {label}
    </span>
  );
}

const vetVisitsConfig: ModuleConfig<VetVisit> = {
  title: 'Vet Visits', entityLabel: 'vet visit', module: 'Health', queryKey: 'vet-visits', api: vetVisitsApi, idKey: 'id',
  referenceKeys: ['animals', 'sheds', 'users'], defaultValues: { visit_date: today() },
  columns: [
    { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.visit_date) },
    { key: 'animal', header: 'Animal', render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'reason', header: 'Reason', render: (r) => r.reason },
    { key: 'vet', header: 'Vet', render: (r) => r.vet_name || '—', hideOnTablet: true },
    { key: 'charges', header: 'Charges', align: 'right', render: (r) => formatCurrency(r.charges) },
    { key: 'follow_up', header: 'Follow-up', render: (r) => <FollowUpCell visit={r} /> },
  ],
  fields: [
    { name: 'animal_id', label: 'Animal (optional)', type: 'select', section: 'Visit', optional: true, optionsKey: 'animals' },
    { name: 'shed_id', label: 'Shed (if farm-level)', type: 'select', section: 'Visit', optional: true, optionsKey: 'sheds' },
    { name: 'visit_date', label: 'Visit Date', type: 'date', section: 'Visit', required: true },
    { name: 'vet_name', label: 'Vet Name', type: 'text', section: 'Visit', optional: true },
    { name: 'reason', label: 'Reason for Visit', type: 'textarea', section: 'Visit', required: true, fullWidth: true },
    { name: 'findings', label: 'Findings', type: 'textarea', section: 'Findings', optional: true, fullWidth: true },
    { name: 'charges', label: 'Charges (₹)', type: 'decimal', section: 'Findings', optional: true, min: 0 },
    { name: 'follow_up_date', label: 'Follow-up Date', type: 'date', section: 'Findings', optional: true },
  ],
};

const treatmentsConfig: ModuleConfig<Treatment> = {
  title: 'Treatments', entityLabel: 'treatment', module: 'Treatment', queryKey: 'treatments', api: treatmentsApi, idKey: 'id',
  referenceKeys: ['animals', 'users'], defaultValues: { start_date: today(), status: 'Ongoing' },
  columns: [
    { key: 'date', header: 'Start Date', primary: true, render: (r) => formatDate(r.start_date) },
    { key: 'animal', header: 'Animal', render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'diagnosis', header: 'Diagnosis', render: (r) => r.diagnosis },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
    { key: 'cost', header: 'Total Cost', align: 'right', render: (r) => formatCurrency(r.total_cost) },
  ],
  fields: [
    { name: 'animal_id', label: 'Animal', type: 'select', section: 'Treatment', required: true, optionsKey: 'animals' },
    { name: 'diagnosis', label: 'Diagnosis / Condition', type: 'text', section: 'Treatment', required: true },
    { name: 'start_date', label: 'Start Date', type: 'date', section: 'Treatment', required: true },
    { name: 'end_date', label: 'End Date', type: 'date', section: 'Treatment', optional: true },
    { name: 'vet_user_id', label: 'Veterinarian', type: 'select', section: 'Treatment', optional: true, optionsKey: 'users' },
    { name: 'status', label: 'Status', type: 'select', section: 'Status', required: true, staticOptions: [
      { value: 'Ongoing', label: 'Ongoing' }, { value: 'Completed', label: 'Completed' }, { value: 'Discontinued', label: 'Discontinued' },
    ] },
    { name: 'outcome', label: 'Outcome', type: 'textarea', section: 'Status', optional: true, fullWidth: true },
  ],
};

const vaccinationsConfig: ModuleConfig<Vaccination> = {
  title: 'Vaccinations', entityLabel: 'vaccination', module: 'Vaccination', queryKey: 'vaccinations', api: vaccinationsApi, idKey: 'id',
  referenceKeys: ['animals', 'vaccines', 'poultryBatches'], defaultValues: { admin_date: today() },
  columns: [
    { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.admin_date) },
    { key: 'animal', header: 'Animal', render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'dose', header: 'Dose', render: (r) => formatNumber(r.dose, 2) },
    { key: 'next', header: 'Next Due', render: (r) => formatDate(r.next_due_date) },
  ],
  fields: [
    { name: 'animal_id', label: 'Animal', type: 'select', section: 'Vaccination', optional: true, optionsKey: 'animals' },
    { name: 'poultry_batch_id', label: 'Poultry Batch (if applicable)', type: 'select', section: 'Vaccination', optional: true, optionsKey: 'poultryBatches' },
    { name: 'vaccine_id', label: 'Vaccine', type: 'select', section: 'Vaccination', required: true, optionsKey: 'vaccines' },
    { name: 'admin_date', label: 'Date', type: 'date', section: 'Vaccination', required: true },
    { name: 'dose', label: 'Dose', type: 'decimal', section: 'Vaccination', optional: true, min: 0 },
    { name: 'unit', label: 'Unit', type: 'text', section: 'Vaccination', optional: true },
    { name: 'lot_no', label: 'Lot Number', type: 'text', section: 'Vaccination', optional: true },
    { name: 'next_due_date', label: 'Next Due Date', type: 'date', section: 'Follow-up', optional: true },
  ],
};

const vaccinationSchedulesConfig: ModuleConfig<VaccinationSchedule> = {
  title: 'Vaccination Schedules', entityLabel: 'schedule', module: 'Vaccination', queryKey: 'vaccination-schedules', api: vaccinationSchedulesApi, idKey: 'id',
  referenceKeys: ['animalTypes', 'vaccines'], defaultValues: { is_mandatory: true, active: true },
  columns: [
    { key: 'name', header: 'Schedule', primary: true, render: (r) => r.schedule_name || '—' },
    { key: 'due_age', header: 'Due Age (days)', render: (r) => r.due_age_days ?? '—' },
    { key: 'repeat', header: 'Repeat Interval (days)', render: (r) => r.repeat_interval_days ?? '—' },
    { key: 'mandatory', header: 'Mandatory', render: (r) => (r.is_mandatory ? 'Yes' : 'No') },
  ],
  fields: [
    { name: 'animal_type_id', label: 'Species', type: 'select', section: 'Schedule', required: true, optionsKey: 'animalTypes' },
    { name: 'vaccine_id', label: 'Vaccine', type: 'select', section: 'Schedule', required: true, optionsKey: 'vaccines' },
    { name: 'schedule_name', label: 'Schedule Name', type: 'text', section: 'Schedule', optional: true },
    { name: 'due_age_days', label: 'Due Age (days after birth)', type: 'number', section: 'Timing', optional: true, min: 0 },
    { name: 'repeat_interval_days', label: 'Booster Interval (days)', type: 'number', section: 'Timing', optional: true, min: 0 },
    { name: 'is_mandatory', label: 'Mandatory', type: 'checkbox', section: 'Timing' },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Timing' },
  ],
};

const dewormingConfig: ModuleConfig<Deworming> = {
  title: 'Deworming', entityLabel: 'deworming record', module: 'Vaccination', queryKey: 'deworming', api: dewormingApi, idKey: 'id',
  referenceKeys: ['animals', 'medicines'], defaultValues: { deworming_date: today() },
  columns: [
    { key: 'animal', header: 'Animal', primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'date', header: 'Date', render: (r) => formatDate(r.deworming_date) },
    { key: 'next', header: 'Next Due', render: (r) => formatDate(r.next_due_date) },
    { key: 'cost', header: 'Cost', align: 'right', render: (r) => formatCurrency(r.cost) },
  ],
  fields: [
    { name: 'animal_id', label: 'Animal', type: 'select', section: 'Deworming', required: true, optionsKey: 'animals' },
    { name: 'deworming_date', label: 'Date', type: 'date', section: 'Deworming', required: true },
    { name: 'medicine_id', label: 'Dewormer', type: 'select', section: 'Deworming', optional: true, optionsKey: 'medicines' },
    { name: 'dose', label: 'Dose', type: 'text', section: 'Deworming', optional: true },
    { name: 'next_due_date', label: 'Next Due Date', type: 'date', section: 'Follow-up', optional: true },
    { name: 'cost', label: 'Cost (₹)', type: 'decimal', section: 'Follow-up', optional: true, min: 0 },
  ],
};

const outbreaksConfig: ModuleConfig<DiseaseOutbreak> = {
  title: 'Disease Outbreaks', entityLabel: 'outbreak', module: 'Health', queryKey: 'disease-outbreaks', api: diseaseOutbreaksApi, idKey: 'id',
  referenceKeys: ['farms', 'sheds'], defaultValues: { start_date: today(), severity: 'Medium', status: 'Active' },
  columns: [
    { key: 'disease', header: 'Disease', primary: true, render: (r) => r.disease_name },
    { key: 'start', header: 'Start Date', render: (r) => formatDate(r.start_date) },
    { key: 'severity', header: 'Severity', render: (r) => <ILMSStatusBadge status={r.severity} size="sm" /> },
    { key: 'affected', header: 'Animals Affected', render: (r) => r.animals_affected_count ?? '—' },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Outbreak', required: true, optionsKey: 'farms' },
    { name: 'disease_name', label: 'Disease', type: 'text', section: 'Outbreak', required: true },
    { name: 'shed_id', label: 'Affected Shed', type: 'select', section: 'Outbreak', optional: true, optionsKey: 'sheds' },
    { name: 'start_date', label: 'Detected On', type: 'date', section: 'Outbreak', required: true },
    { name: 'severity', label: 'Severity', type: 'select', section: 'Outbreak', required: true, staticOptions: [
      { value: 'Low', label: 'Low' }, { value: 'Medium', label: 'Medium' }, { value: 'High', label: 'High' }, { value: 'Critical', label: 'Critical' },
    ] },
    { name: 'animals_affected_count', label: 'Animals Affected', type: 'number', section: 'Impact', optional: true, min: 0 },
    { name: 'containment_measures', label: 'Containment Measures', type: 'textarea', section: 'Impact', optional: true, fullWidth: true },
    { name: 'status', label: 'Status', type: 'select', section: 'Impact', required: true, staticOptions: [
      { value: 'Active', label: 'Active' }, { value: 'Contained', label: 'Contained' }, { value: 'Closed', label: 'Closed' },
    ] },
    { name: 'end_date', label: 'Closed On', type: 'date', section: 'Impact', optional: true },
  ],
};

const growthConfig: ModuleConfig<GrowthRecord> = {
  title: 'Growth Records', entityLabel: 'growth record', module: 'Weight', queryKey: 'growth-records', api: growthRecordsApi, idKey: 'id',
  referenceKeys: ['animals'], addLabel: 'Record Growth', defaultValues: { record_date: today() },
  columns: [
    { key: 'animal', header: 'Animal', primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'date', header: 'Date', render: (r) => formatDate(r.record_date) },
    { key: 'weight', header: 'Weight (kg)', align: 'right', render: (r) => formatNumber(r.weight_kg, 1) },
    { key: 'gain', header: 'Change (kg)', align: 'right', render: (r) => formatNumber(r.weight_gain_kg, 1) },
    { key: 'bcs', header: 'BCS', render: (r) => formatNumber(r.body_condition_score, 1), hideOnTablet: true },
  ],
  fields: [
    { name: 'animal_id', label: 'Animal', type: 'select', section: 'Growth', required: true, optionsKey: 'animals' },
    { name: 'record_date', label: 'Date', type: 'date', section: 'Growth', required: true },
    { name: 'weight_kg', label: 'Weight (kg)', type: 'decimal', section: 'Growth', optional: true, min: 0 },
    { name: 'height_cm', label: 'Height / Length (cm)', type: 'decimal', section: 'Growth', optional: true, min: 0 },
    { name: 'body_condition_score', label: 'Body Condition Score (1-5)', type: 'decimal', section: 'Growth', optional: true, min: 0 },
  ],
};

const medicineConfig: ModuleConfig<MedicineMaster> = {
  title: 'Medicines', entityLabel: 'medicine', module: 'Health', queryKey: 'medicine-master', api: medicineMasterApi, idKey: 'id',
  defaultValues: { active: true },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'unit', header: 'Unit', render: (r) => r.unit || '—' },
    { key: 'withdrawal', header: 'Withdrawal (days)', render: (r) => r.withdrawal_period_days ?? '—' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', section: 'Details', required: true },
    { name: 'unit', label: 'Unit', type: 'text', section: 'Details', optional: true },
    { name: 'default_dose', label: 'Default Dose', type: 'text', section: 'Details', optional: true },
    { name: 'withdrawal_period_days', label: 'Milk/Meat Withdrawal Period (days)', type: 'number', section: 'Details', optional: true, min: 0 },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
  ],
};

const vaccineConfig: ModuleConfig<VaccineMaster> = {
  title: 'Vaccines', entityLabel: 'vaccine', module: 'Health', queryKey: 'vaccine-master', api: vaccineMasterApi, idKey: 'id',
  referenceKeys: ['animalTypes'], defaultValues: { active: true },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'unit', header: 'Dose Unit', render: (r) => r.dose_unit || '—' },
    { key: 'schedule', header: 'Schedule', render: (r) => r.schedule_days || '—' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', section: 'Details', required: true },
    { name: 'animal_type_id', label: 'Target Species', type: 'select', section: 'Details', optional: true, optionsKey: 'animalTypes' },
    { name: 'dose_unit', label: 'Dose Unit', type: 'text', section: 'Details', optional: true },
    { name: 'schedule_days', label: 'Schedule Note', type: 'text', section: 'Details', optional: true, helperText: 'e.g. 0, 21, 42 for a multi-dose schedule.' },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
  ],
};

export default function HealthPage() {
  return (
    <TabbedPage
      title="Health"
      subtitle="Disease, treatment, vaccination, deworming and outbreak management (WF-04)."
      tabs={[
        { id: 'health-records', label: 'Health Records', render: () => <ResourceModule config={healthRecordsConfig} /> },
        { id: 'vet-visits', label: 'Vet Visits', render: () => <ResourceModule config={vetVisitsConfig} /> },
        { id: 'treatments', label: 'Treatments', render: () => <ResourceModule config={treatmentsConfig} /> },
        { id: 'vaccinations', label: 'Vaccinations', render: () => <ResourceModule config={vaccinationsConfig} /> },
        { id: 'vaccination-schedules', label: 'Schedules', render: () => <ResourceModule config={vaccinationSchedulesConfig} /> },
        { id: 'deworming', label: 'Deworming', render: () => <ResourceModule config={dewormingConfig} /> },
        { id: 'outbreaks', label: 'Disease Outbreaks', render: () => <ResourceModule config={outbreaksConfig} /> },
        { id: 'growth', label: 'Growth', render: () => <ResourceModule config={growthConfig} /> },
        { id: 'medicines', label: 'Medicines', render: () => <ResourceModule config={medicineConfig} /> },
        { id: 'vaccines', label: 'Vaccines', render: () => <ResourceModule config={vaccineConfig} /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Health', healthRecordsConfig, vetVisitsConfig, treatmentsConfig, vaccinationsConfig, vaccinationSchedulesConfig, dewormingConfig, outbreaksConfig, growthConfig, medicineConfig, vaccineConfig);
